import { useState, type ReactNode } from 'react'
import {
  Amount,
  Button,
  IconChatMessage,
  IconWallet,
  Stack,
  Text,
  unstable_Cell as Cell,
  unstable_CellList as CellList,
} from '@n26/nxd'
import { useTranslation } from 'react-i18next'
import { StepHeader } from '@/features/transfer/components/StepHeader'
import { Pinput } from '@/shared/components/Pinput'
import { ErrorMessage } from '@/shared/components/ErrorMessage'
import {
  generateColor,
  generateInitials,
} from '@/shared/components/ProfileInitials'
import { PIN_LENGTH } from '@/features/transfer/model/constants'
import type { TransferFormData } from '@/features/transfer/model/types'
import { validatePin } from '@/features/transfer/model/validation'
import styles from './ReviewStep.module.css'

type ReviewStepProps = {
  title: string
  data: TransferFormData
  accountName: string
  amountLabel: string
  onBack: () => void
  onCancel: () => void
  /** Called with the entered PIN once it passes the local length check. */
  onConfirm: (pin: string) => void
  isSubmitting?: boolean
  /** Server-side error from the initiation call; takes precedence over the local length error. */
  error?: ReactNode
}

const Line = ({ label, value }: { label: string; value: string }) => (
  <Stack direction='vertical' space='4'>
    <Text variant='small' color='neutral-alternate'>
      {label}
    </Text>
    <Text>{value}</Text>
  </Stack>
)

export const ReviewStep = ({
  title,
  data,
  accountName,
  amountLabel,
  onBack,
  onCancel,
  onConfirm,
  isSubmitting = false,
  error,
}: ReviewStepProps) => {
  const { t } = useTranslation()
  const [pin, setPin] = useState('')
  const [pinError, setPinError] = useState<string | null>(null)

  const handleConfirm = () => {
    if (isSubmitting) return
    const lengthError = validatePin(pin)
    setPinError(lengthError)
    if (!lengthError) onConfirm(pin)
  }

  return (
    <Stack direction='vertical' space='32'>
      <StepHeader title={title} onBack={onBack} />

      <CellList>
        <Cell>
          <Cell.Spot color='teal'>
            <IconWallet />
          </Cell.Spot>
          <Cell.Main>
            <Line label={t('transfer.review.fromLabel')} value={accountName} />
          </Cell.Main>
          <Amount color='neutral'>{amountLabel}</Amount>
        </Cell>
        <Cell>
          <Cell.Spot color={generateColor(data.name)}>
            {generateInitials(data.name)}
          </Cell.Spot>
          <Cell.Main>
            <Stack direction='vertical' space='4'>
              <Text variant='small' color='neutral-alternate'>
                {t('transfer.review.toLabel')}
              </Text>
              <Text>{data.name}</Text>
              <Text variant='small' color='neutral-alternate'>
                {data.iban}
              </Text>
            </Stack>
          </Cell.Main>
        </Cell>
        <Cell>
          <Cell.Icon icon={<IconChatMessage />} aria-hidden />
          <Cell.Main>
            <Line
              label={t('transfer.review.referenceLabel')}
              value={data.reference || t('transfer.review.defaultReference')}
            />
          </Cell.Main>
        </Cell>
      </CellList>

      <Stack direction='vertical' space='12'>
        <Text>{t('transfer.review.confirmationPin')}</Text>
        <div className={styles.pinField}>
          <Pinput
            length={PIN_LENGTH}
            value={pin}
            onChange={next => {
              setPin(next)
              if (pinError) setPinError(null)
            }}
            label={t('transfer.review.pinFieldLabel')}
            mask
            disabled={isSubmitting}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
        </div>
        {pinError ?? error ? (
          <ErrorMessage id='pin-error'>{pinError ?? error}</ErrorMessage>
        ) : null}
      </Stack>

      <Stack direction='horizontal' space='24' align='center'>
        <Button
          type='button'
          variant='secondary'
          color='teal'
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {t('transfer.review.cancelButton')}
        </Button>
        <Button
          type='button'
          variant='primary'
          color='teal'
          onClick={handleConfirm}
          loading={isSubmitting}
        >
          {t('transfer.review.sendButton')}
        </Button>
      </Stack>
    </Stack>
  )
}
